// Validation rules for pricing updates
const validationRules = {
  cableBasePrices: {
    minPrice: 0.1,
    maxPrice: 100,
    minPercentageChange: -50,
    maxPercentageChange: 200
  },
  powerWireGaugePricing: {
    minPrice: 0.1,
    maxPrice: 100,
    minPercentageChange: -50,
    maxPercentageChange: 200
  },
  wyeCablePricing: {
    minPrice: 0.1,
    maxPrice: 100,
    minPercentageChange: -50,
    maxPercentageChange: 200
  },
  panelMountPricing: {
    minPartCost: 0.1,
    maxPartCost: 1000,
    minLaborCost: 0,
    maxLaborCost: 100,
    minPercentageChange: -50,
    maxPercentageChange: 200
  },
  inlinePricing: {
    minPartCost: 0.1,
    maxPartCost: 1000,
    minLaborCost: 0,
    maxLaborCost: 100,
    minPercentageChange: -50,
    maxPercentageChange: 200
  },
  finishingPricing: {
    minPrice: 0,
    maxPrice: 100,
    minPercentageChange: -50,
    maxPercentageChange: 200
  },
  laborCosts: {
    minCost: 0,
    maxCost: 100,
    minPercentageChange: -50,
    maxPercentageChange: 200
  },
  markups: {
    minMarkup: 0,
    maxMarkup: 5, // 500%
    minPercentageChange: -50,
    maxPercentageChange: 200
  }
};

// Validate a pricing update
export const validatePricingUpdate = (update) => {
  const errors = [];
  const rules = validationRules[update.type];

  if (!rules) {
    errors.push('Invalid update type');
    return { isValid: false, errors };
  }

  if (update.method === 'percentage') {
    if (update.value < rules.minPercentageChange) {
      errors.push(`Percentage change cannot be less than ${rules.minPercentageChange}%`);
    }
    if (update.value > rules.maxPercentageChange) {
      errors.push(`Percentage change cannot be greater than ${rules.maxPercentageChange}%`);
    }
  } else if (update.method === 'fixed') {
    if (update.value < 0) {
      errors.push('Fixed amount change must be positive');
    }
  } else {
    errors.push('Invalid update method');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Validate a complete pricing configuration
export const validatePricingConfig = (config) => {
  const errors = [];

  // Validate cable base prices (flat)
  Object.entries(config.cableBasePrices || {}).forEach(([type, price]) => {
    if (price < validationRules.cableBasePrices.minPrice) {
      errors.push(`${type} price cannot be less than $${validationRules.cableBasePrices.minPrice}`);
    }
    if (price > validationRules.cableBasePrices.maxPrice) {
      errors.push(`${type} price cannot be greater than $${validationRules.cableBasePrices.maxPrice}`);
    }
  });

  // Validate power wire gauge pricing (flat)
  Object.entries(config.powerWireGaugePricing || {}).forEach(([gauge, price]) => {
    if (price < validationRules.powerWireGaugePricing.minPrice) {
      errors.push(`${gauge} price cannot be less than $${validationRules.powerWireGaugePricing.minPrice}`);
    }
    if (price > validationRules.powerWireGaugePricing.maxPrice) {
      errors.push(`${gauge} price cannot be greater than $${validationRules.powerWireGaugePricing.maxPrice}`);
    }
  });

  // Validate Wye cable pricing (flat)
  Object.entries(config.wyeCablePricing || {}).forEach(([cableType, price]) => {
    if (price < validationRules.wyeCablePricing.minPrice) {
      errors.push(`${cableType} Wye cable price cannot be less than $${validationRules.wyeCablePricing.minPrice}`);
    }
    if (price > validationRules.wyeCablePricing.maxPrice) {
      errors.push(`${cableType} Wye cable price cannot be greater than $${validationRules.wyeCablePricing.maxPrice}`);
    }
  });

  // Validate panel mount pricing
  Object.entries(config.panelMountPricing || {}).forEach(([connector, pricing]) => {
    if (!pricing || typeof pricing !== 'object') {
      errors.push(`${connector} pricing must be an object with partCost and laborCost`);
      return;
    }
    if (typeof pricing.partCost !== 'number' || pricing.partCost < validationRules.panelMountPricing.minPartCost) {
      errors.push(`${connector} part cost cannot be less than $${validationRules.panelMountPricing.minPartCost}`);
    }
    if (typeof pricing.partCost !== 'number' || pricing.partCost > validationRules.panelMountPricing.maxPartCost) {
      errors.push(`${connector} part cost cannot be greater than $${validationRules.panelMountPricing.maxPartCost}`);
    }
    if (typeof pricing.laborCost !== 'number' || pricing.laborCost < validationRules.panelMountPricing.minLaborCost) {
      errors.push(`${connector} labor cost cannot be less than $${validationRules.panelMountPricing.minLaborCost}`);
    }
    if (typeof pricing.laborCost !== 'number' || pricing.laborCost > validationRules.panelMountPricing.maxLaborCost) {
      errors.push(`${connector} labor cost cannot be greater than $${validationRules.panelMountPricing.maxLaborCost}`);
    }
  });

  // Validate inline pricing
  Object.entries(config.inlinePricing || {}).forEach(([connector, pricing]) => {
    if (!pricing || typeof pricing !== 'object') {
      errors.push(`${connector} pricing must be an object with partCost and laborCost`);
      return;
    }
    if (typeof pricing.partCost !== 'number' || pricing.partCost < validationRules.inlinePricing.minPartCost) {
      errors.push(`${connector} part cost cannot be less than $${validationRules.inlinePricing.minPartCost}`);
    }
    if (typeof pricing.partCost !== 'number' || pricing.partCost > validationRules.inlinePricing.maxPartCost) {
      errors.push(`${connector} part cost cannot be greater than $${validationRules.inlinePricing.maxPartCost}`);
    }
    if (typeof pricing.laborCost !== 'number' || pricing.laborCost < validationRules.inlinePricing.minLaborCost) {
      errors.push(`${connector} labor cost cannot be less than $${validationRules.inlinePricing.minLaborCost}`);
    }
    if (typeof pricing.laborCost !== 'number' || pricing.laborCost > validationRules.inlinePricing.maxLaborCost) {
      errors.push(`${connector} labor cost cannot be greater than $${validationRules.inlinePricing.maxLaborCost}`);
    }
  });

  // Validate finishing pricing (flat)
  Object.entries(config.finishingPricing || {}).forEach(([option, price]) => {
    if (price < validationRules.finishingPricing.minPrice) {
      errors.push(`${option} price cannot be less than $${validationRules.finishingPricing.minPrice}`);
    }
    if (price > validationRules.finishingPricing.maxPrice) {
      errors.push(`${option} price cannot be greater than $${validationRules.finishingPricing.maxPrice}`);
    }
  });

  // Validate labor cost (single value)
  if (typeof config.laborCost === 'number') {
    if (config.laborCost < validationRules.laborCosts.minCost) {
      errors.push(`Labor cost cannot be less than $${validationRules.laborCosts.minCost}`);
    }
    if (config.laborCost > validationRules.laborCosts.maxCost) {
      errors.push(`Labor cost cannot be greater than $${validationRules.laborCosts.maxCost}`);
    }
  }

  // Validate markups
  Object.entries(config.markups || {}).forEach(([category, markup]) => {
    if (markup < validationRules.markups.minMarkup) {
      errors.push(`${category} markup cannot be less than ${validationRules.markups.minMarkup * 100}%`);
    }
    if (markup > validationRules.markups.maxMarkup) {
      errors.push(`${category} markup cannot be greater than ${validationRules.markups.maxMarkup * 100}%`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Validate a specific price value
export const validatePrice = (type, value) => {
  const rules = validationRules[type];
  if (!rules) return { isValid: false, error: 'Invalid price type' };

  if (value < rules.minPrice) {
    return { isValid: false, error: `Price cannot be less than $${rules.minPrice}` };
  }
  if (value > rules.maxPrice) {
    return { isValid: false, error: `Price cannot be greater than $${rules.maxPrice}` };
  }

  return { isValid: true };
}; 